import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useGetUserPartnersQuery } from '@/api/queries/user';
import { Invite } from '@/components/Partners/Invite/Invite';
import { NoInvitePartners } from '@/components/Partners/NoInvitePartners/NoInvitePartners';
import { StringSmall, StringXSmall } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { Picture } from '@/components/ui/Picture/Picture';

import { PartnerRevenueItem } from './PartnerRevenueItem';

import css from './styles.module.scss';

import usdPng from '/img/icons/usd.png';
import usdWebp from '/img/icons/usd.webp';

interface RevenueTierDatailProps {
  level: number;
}

const RevenueTierDatail: FC<RevenueTierDatailProps> = ({ level }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useGetUserPartnersQuery({ page, level });
  const { t } = useTranslation();

  const showMoreButtonIsActive = (data?.total || 0) > page;

  return (
    <>
      <div className="w-full flex flex-col px-4">
        <div className="flex items-center justify-between h-9">
          <div className="flex items-center gap-1">
            <StringXSmall weight="medium" className="!text-white/60">
              {t('Total Partners:')}
            </StringXSmall>
            <StringSmall weight="bold">{data?.referrals || 0}</StringSmall>
          </div>
          <div className="flex items-center gap-1">
            <Picture
              className="w-6 h-6 shrink-0"
              img={usdPng}
              webp={usdWebp}
              alt="toncoin"
            />
            <StringSmall weight="bold">{data?.earnings || 0}</StringSmall>
          </div>
        </div>
        <div className="flex flex-col gap-[2px] h-[25rem] overflow-y-auto pt-3 border-t border-t-white/20">
          {data?.partners.length === 0 ? (
            <NoInvitePartners className="h-full pb-4" />
          ) : (
            data?.partners.map((props) => (
              <PartnerRevenueItem key={props.id} {...props} />
            ))
          )}
        </div>
      </div>
      {data?.partners && (
        <>
          {data.partners.length === 0 ? (
            <Invite className="px-4" />
          ) : (
            <div className={cn('w-full px-4 py-2 bg-main-bg', css.shadow)}>
              <MainButton
                isLoading={isLoading}
                inactive={!showMoreButtonIsActive}
                disabled={!showMoreButtonIsActive || isLoading}
                onClick={() => setPage((state) => state + 1)}
                text={t('Show more')}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export { RevenueTierDatail };
